import React from 'react';
import type { FC } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { AutoScaledImage } from '@helpcenter/auto-scaled-image';
import { isIncreasedFlexibilityEnabled, isUseHelpCenterRefresh } from '@helpcenter/feature-flags';
import * as fonts from '@helpcenter/styles/src/fonts';
import FallbackSuggestedRequestForms from 'assets/images/fallback-suggested-request-forms.svg';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { getHelpCenterTopBarSplitFlag } from 'state/selectors/help-center';
import { isLoggedIn } from 'state/selectors/user';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import recentFormsMessages from 'view/help-center/recent-forms/messages';
import { IconCardLayout } from 'view/layout/icon-card-layout';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import EmptyState from '@atlaskit/empty-state/empty-state';

import { Stack } from '@atlaskit/primitives/stack';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { media } from '@atlaskit/primitives/responsive/media';
import type { suggestedRequestForms_HelpLayoutListOfHelpCenterSuggestedRequestFormsAtomicElement$key as SuggestedRequestedForms } from './__generated__/suggestedRequestForms_HelpLayoutListOfHelpCenterSuggestedRequestFormsAtomicElement.graphql';
import messages from './messages';
import { RecentFormsListItem } from './recent-forms-list-item/recent-forms-list-item';

const selector = createSelector(
	isLoggedIn,
	getHelpCenterTopBarSplitFlag,
	(isUserLoggedIn, hasTopBarBeenSplit) => ({
		isUserLoggedIn,
		hasTopBarBeenSplit,
	}),
);

const Component: FC<{
	forms: SuggestedRequestedForms;
	isUserLoggedIn: boolean;
	hasTopBarBeenSplit?: boolean;
}> = ({ forms, isUserLoggedIn, hasTopBarBeenSplit }) => {
	di(useIsPageInEditMode);
	di(useIsPageInPreviewMode);
	di(isUseHelpCenterRefresh);
	di(isIncreasedFlexibilityEnabled);

	const data = useFragment<SuggestedRequestedForms>(
		graphql`
			fragment suggestedRequestForms_HelpLayoutListOfHelpCenterSuggestedRequestFormsAtomicElement on HelpLayoutSuggestedRequestFormsListElement {
				suggestedRequestTypes {
					...recentFormsListItem_on_RequestForm
					id
					iconUrl
				}
				visualConfig {
					titleColor
				}
			}
		`,
		forms,
	);
	const suggestedRequestTypes = data.suggestedRequestTypes;
	const { formatMessage } = useIntl();

	const [isPageInEditMode] = useIsPageInEditMode();
	const [isPageInPreviewMode] = useIsPageInPreviewMode();

	if (!suggestedRequestTypes?.length && isPageInEditMode && !isPageInPreviewMode) {
		return (
			<Box xcss={nonDraggableStyle}>
				<EmptyState
					header=""
					maxImageWidth={200}
					description={formatMessage(messages.emptyStateDescription)}
					imageUrl={FallbackSuggestedRequestForms}
					width="wide"
				/>
			</Box>
		);
	}

	if (suggestedRequestTypes?.length) {
		let stackXCSS = containerStyles;
		if (isUseHelpCenterRefresh() && hasTopBarBeenSplit) {
			stackXCSS = containerStylesVisualRefreshV2;
		}

		return (
			<ElementRoot elementWidth={ElementWidth.FULL_WIDTH}>
				<Stack xcss={stackXCSS}>
					{/* TODO: remove connection to the user selector once we have the label field on the Element. */}
					<Header
						textColor={
							(isIncreasedFlexibilityEnabled() && data.visualConfig?.titleColor) || undefined
						}
					>
						{isUserLoggedIn
							? formatMessage(recentFormsMessages.headerTitle)
							: formatMessage(recentFormsMessages.headerTitleAnon)}
					</Header>
					<Stack
						as="ul"
						xcss={[
							listStyles,
							isIncreasedFlexibilityEnabled() && listStylesWithIncreasedFlexibility,
						]}
					>
						{suggestedRequestTypes.map((requestForm) => {
							const Icon =
								requestForm.iconUrl == null ? null : (
									<AutoScaledImage src={requestForm.iconUrl} alt="" />
								);
							const LinkAndDescription = <RecentFormsListItem requestForm={requestForm} />;
							return (
								<Box as="li" xcss={itemStyles} key={`recentRequestSuggestion_${requestForm.id}`}>
									<IconCardLayout padding={0} leftIcon={Icon} content={LinkAndDescription} />
								</Box>
							);
						})}
					</Stack>
				</Stack>
			</ElementRoot>
		);
	}
	return null;
};

const listStyles = xcss({
	marginTop: 'space.0',
	paddingLeft: 'space.0',
	rowGap: 'space.150',
	listStyleType: 'none',
});

const listStylesWithIncreasedFlexibility = xcss({
	borderRadius: 'radius.small',
	backgroundColor: 'elevation.surface.raised',
	boxShadow: 'elevation.shadow.raised',
	padding: 'space.300',
});

const containerStyles = xcss({
	rowGap: 'space.200',
	width: '100%',
	maxWidth: '840px',
	marginInline: 'auto',
	boxSizing: 'border-box',
	paddingInline: 'space.600',
});

const containerStylesVisualRefreshV2 = xcss({
	rowGap: 'space.200',
	width: '100%',
	maxWidth: '840px',
	boxSizing: 'border-box',
	[media.above.sm]: {
		maxWidth: '744px',
	},
});

const itemStyles = xcss({
	wordWrap: 'break-word',
	marginTop: 'space.0',
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Header = styled.h2<{ textColor?: string }>`
	${fonts.h500};
	color: ${({ textColor }) => textColor};
`;

const nonDraggableStyle = xcss({
	pointerEvents: 'none',
});

export const SuggestedRequestForms = connect(selector)(Component);
